import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  Distance,
  HoursPerWeekPreference,
  Option,
  SalaryPeriod,
  UserDreamProfessionModel,
  UserPreferredEducationLevel,
  VacancyPreferencesModel,
} from '@digilize/shared/definitions/src';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, withLatestFrom } from 'rxjs';

import { createOptions } from '../../helpers/helpers';
import { ProfessionsService } from '../../services/professions.service';
import { ProfileChangesService } from '../../services/profile-changes.service';
import { SharedAccountDataService } from '../../services/shared-account-data.service';

@Component({
  selector: 'bwc-work-preferences',
  templateUrl: './work-preferences.component.html',
  styleUrls: ['./work-preferences.component.scss'],
})
export class WorkPreferencesComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly professionsService = inject(ProfessionsService);
  private readonly profileChangesService = inject(ProfileChangesService);
  private readonly sharedAccountDataService = inject(SharedAccountDataService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  form: FormGroup;

  salaryPeriodControl = new FormControl('');
  workHoursControl = new FormControl('');
  educationLevelControl = new FormControl('');
  occupationSearchControl = new FormControl('');
  salaryPeriodOptions: Option[] = [];
  workHoursOptions: Option[] = [];
  educationLevelOptions: Option[] = [];
  professionOptions: Option[] = [];
  dreamProfessions: UserDreamProfessionModel[] = [];

  distanceSteps: { value: Distance; labelKey: string }[] = [
    { value: Distance.Upto10km, labelKey: 'work_preferences.distance_10' },
    { value: Distance.Upto25km, labelKey: 'work_preferences.distance_25' },
    { value: Distance.Upto50km, labelKey: 'work_preferences.distance_50' },
    { value: Distance.Any, labelKey: 'work_preferences.distance_50_plus' },
  ];
  distanceSliderValue = 0;
  distanceLabel = '';
  showInfoTooltip = false;

  private salaryPeriodMap: { value: SalaryPeriod; labelKey: string }[] = [
    { value: SalaryPeriod.Monthly, labelKey: 'work_preferences.per_month' },
    // { value: SalaryPeriod.Yearly, labelKey: 'work_preferences.per_year' },
  ];

  private workHoursMap: { value: HoursPerWeekPreference; labelKey: string }[] = [
    { value: HoursPerWeekPreference.H32_40, labelKey: 'work_preferences.fulltime' },
    { value: HoursPerWeekPreference.H24_32, labelKey: 'work_preferences.parttime_24_32' },
    { value: HoursPerWeekPreference.H0_24, labelKey: 'work_preferences.parttime_0_24' },
    { value: HoursPerWeekPreference.HFlexible, labelKey: 'work_preferences.flexible' },
  ];

  private educationLevelMap: { value: UserPreferredEducationLevel; labelKey: string }[] = [
    { value: UserPreferredEducationLevel.Lbo, labelKey: 'work_preferences.education_level_lbo' },
    { value: UserPreferredEducationLevel.Vmbo, labelKey: 'work_preferences.education_level_vmbo' },
    { value: UserPreferredEducationLevel.Havo, labelKey: 'work_preferences.education_level_havo' },
    { value: UserPreferredEducationLevel.Vwo, labelKey: 'work_preferences.education_level_vwo' },
    { value: UserPreferredEducationLevel.Mbo, labelKey: 'work_preferences.education_level_mbo' },
    { value: UserPreferredEducationLevel.Hbo, labelKey: 'work_preferences.education_level_hbo' },
    { value: UserPreferredEducationLevel.Wo, labelKey: 'work_preferences.education_level_wo' },
    { value: UserPreferredEducationLevel.Phd, labelKey: 'work_preferences.education_level_phd' },
  ];

  ngOnInit(): void {
    this.form = this.fb.group({
      distance: [Distance.Unknown],
      city: [null as string | null],
      salary: [null as number | null],
      salary_period: [SalaryPeriod.Monthly],
      hours_range: [null as HoursPerWeekPreference | null],
      education_level: [null as UserPreferredEducationLevel | null],
    });

    this.cityControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.notifyChanges());

    this.loadProfessions('');
    this.loadOptionsAndPreferences();
  }

  get cityControl(): FormControl<string> {
    return this.form.get('city') as FormControl<string>;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDistanceSliderChange(sliderValue: number): void {
    this.distanceSliderValue = sliderValue;
    const step = this.distanceSteps[sliderValue];
    if (step) {
      this.form.patchValue({ distance: step.value });
      this.updateDistanceLabel();
      this.notifyChanges();
    }
  }

  onSalaryPeriodSelected(option: Option): void {
    this.form.patchValue({ salary_period: Number(option.val) });
    this.notifyChanges();
  }

  onWorkHoursSelected(option: Option): void {
    this.form.patchValue({ hours_range: Number(option.val) });
    this.notifyChanges();
  }

  onEducationLevelSelected(option: Option): void {
    this.form.patchValue({ education_level: Number(option.val) });
    this.notifyChanges();
  }

  onProfessionSelected(option: Option): void {
    const alreadyAdded = this.dreamProfessions.some((p) => p.position_id === option.id);
    if (alreadyAdded) {
      return;
    }

    const newProfession: UserDreamProfessionModel = {
      id: '',
      name: option.text,
      position_id: option.id ?? null,
      order: this.dreamProfessions.length,
    };

    this.dreamProfessions = [...this.dreamProfessions, newProfession];
    this.occupationSearchControl.setValue('');
    this.notifyChanges();
  }

  onSalaryInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^\d]/g, '');
    if (cleaned !== input.value) {
      input.value = cleaned;
    }
    this.form.patchValue({ salary: cleaned === '' ? null : Number(cleaned) }, { emitEvent: false });
  }

  onSalaryChanged(): void {
    const raw = this.form.get('salary')?.value;
    const intVal = raw == null || raw === '' ? null : Math.trunc(Number(raw));
    const safe = intVal != null && Number.isFinite(intVal) ? intVal : null;
    this.form.patchValue({ salary: safe }, { emitEvent: false });
    this.notifyChanges();
  }
  onProfessionSearchChanged(searchValue: string): void {
    this.loadProfessions(searchValue);
  }

  removeOccupation(index: number): void {
    this.dreamProfessions = this.dreamProfessions.filter((_, i) => i !== index).map((p, i) => ({ ...p, order: i }));
    this.notifyChanges();
  }

  private notifyChanges(): void {
    const formValue = this.form.getRawValue();
    const payload: VacancyPreferencesModel = {
      distance: formValue.distance ?? Distance.Unknown,
      city: formValue.city || null,
      salary: formValue.salary,
      salary_period: formValue.salary_period ?? SalaryPeriod.Monthly,
      hours_range: formValue.hours_range ?? HoursPerWeekPreference.HFlexible,
      education_level: formValue.education_level ?? null,
      dream_professions: this.dreamProfessions,
    };

    this.profileChangesService.setVacancyPreferences(payload);
  }

  private loadOptionsAndPreferences(): void {
    const translationKeys = [
      ...this.distanceSteps.map((d) => d.labelKey),
      ...this.salaryPeriodMap.map((s) => s.labelKey),
      ...this.workHoursMap.map((h) => h.labelKey),
      ...this.educationLevelMap.map((e) => e.labelKey),
    ];
    this.sharedAccountDataService.cv$
      .pipe(takeUntil(this.destroy$), withLatestFrom(this.translateService.get(translationKeys)))
      .subscribe({
        next: ([accountCv, translations]) => {
          const prefs = accountCv.vacancy_preferences;
          if (prefs) {
            const city = prefs.city || accountCv?.city || null;
            this.form.patchValue({
              distance: prefs.distance,
              city,
              salary: prefs.salary,
              salary_period: prefs.salary_period,
              hours_range: prefs.hours_range,
              education_level: prefs.education_level,
            });
            this.dreamProfessions = prefs.dream_professions ?? [];

            const sliderIndex = this.distanceSteps.findIndex((s) => s.value === prefs.distance);
            this.distanceSliderValue = sliderIndex >= 0 ? sliderIndex : 0;

            this.setDisplayControl(this.salaryPeriodControl, this.salaryPeriodMap, prefs.salary_period, translations);
            this.setDisplayControl(this.workHoursControl, this.workHoursMap, prefs.hours_range, translations);
            this.setDisplayControl(
              this.educationLevelControl,
              this.educationLevelMap,
              prefs.education_level,
              translations
            );
          } else if (accountCv?.city) {
            this.form.patchValue({ city: accountCv.city });
          }

          this.updateDistanceLabel(translations);

          this.salaryPeriodOptions = this.salaryPeriodMap.map((s) => ({
            id: String(s.value),
            text: translations[s.labelKey],
            val: String(s.value),
          }));
          this.workHoursOptions = this.workHoursMap.map((h) => ({
            id: String(h.value),
            text: translations[h.labelKey],
            val: String(h.value),
          }));
          this.educationLevelOptions = this.educationLevelMap.map((e) => ({
            id: String(e.value),
            text: translations[e.labelKey],
            val: String(e.value),
          }));
        },
        error: (err) => console.error('Error loading work preferences:', err),
      });
  }

  private updateDistanceLabel(translations?: Record<string, string>): void {
    const step = this.distanceSteps[this.distanceSliderValue];
    if (!step) return;

    if (translations && translations[step.labelKey]) {
      this.distanceLabel = translations[step.labelKey];
    } else {
      this.translateService.get(step.labelKey).subscribe((label) => {
        this.distanceLabel = label;
      });
    }
  }

  private setDisplayControl<T>(
    control: FormControl<string>,
    map: { value: T; labelKey: string }[],
    enumValue: T,
    translations: Record<string, string>
  ): void {
    const entry = map.find((m) => m.value === enumValue);
    if (entry) {
      control.setValue(translations[entry.labelKey]);
    }
  }

  private loadProfessions(searchValue: string): void {
    this.professionsService
      .getListOfProfessions(searchValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.professionOptions = createOptions(response.data);
        },
        error: (err) => console.error('Error loading professions:', err),
      });
  }
}
